import React, { useCallback, useEffect, useMemo } from 'react';
import { RouteComponentProps } from 'react-router';
import { isNull } from 'lodash/fp';

import { useAppDispatch, useAppSelector } from '../../../../hooks/react-redux';
import { fetchAIAgreementTypesStarted } from '../queries/store/actions';
import { fetchAllDocumentNamesStarted } from '../../documents/store';
import { fetchAllClientsStarted } from '../../clients/store/actions';
import { useFetchStarted } from '../../../../hooks/useFetchStarted';
import { getAllAIAgreementTypes, getIsFetchingAgreementTypes } from '../queries/store/selectors';
import { getSelectedAgreementTypeId, getUploadModalOpen, setSelectedAgreementTypeId, toggleFewShotDocumentsUploadModal } from './store';
import styles from './FewShotDocuments.module.scss';
import { PageLoadingDots } from '../../../shared/loading-dots/PageLoadingDots';
import { FewShotDocumentsTable } from './FewShotDocumentsTable';
import { FewShotAgreementTypes } from './FewShotAgreementTypes';
import { UploadModal } from './UploadModal';

export interface FewShotDocumentsRouteParams {
    agreementTypeId: string | undefined;
}

export const FewShotDocuments: React.FC<RouteComponentProps<FewShotDocumentsRouteParams>> = ({ match }) => {
    const dispatch = useAppDispatch();

    useFetchStarted([fetchAllDocumentNamesStarted(), fetchAIAgreementTypesStarted(), fetchAllClientsStarted()]);

    const agreementTypeId = useMemo(() => match.params.agreementTypeId ? parseInt(match.params.agreementTypeId) : undefined, [match]);

    const aiAgreementTypes = useAppSelector(getAllAIAgreementTypes);
    const isFetchingAgreementTypes = useAppSelector(getIsFetchingAgreementTypes);
    const selectedAgreementTypeId = useAppSelector(getSelectedAgreementTypeId);
    const uploadModalIsOpen = useAppSelector(getUploadModalOpen);

    const agreementTypeIdInParamsExists = useMemo(() => (agreementTypeId && aiAgreementTypes.map(({ agreementTypeId }) => agreementTypeId).includes(agreementTypeId)) || false, [agreementTypeId, aiAgreementTypes]);

    const selectAgreementType = useCallback((agreementTypeId: number) => dispatch(setSelectedAgreementTypeId(agreementTypeId)), [dispatch]);

    const closeUploadModal = useCallback(() => dispatch(toggleFewShotDocumentsUploadModal(false)), [dispatch]);

    useEffect(() => {
        if (isNull(selectedAgreementTypeId) && agreementTypeIdInParamsExists) {
            selectAgreementType(agreementTypeId!);
        }
    }, [dispatch, selectedAgreementTypeId, agreementTypeId, agreementTypeIdInParamsExists, selectAgreementType]);

    const pageContent = useMemo(() => {
        if (isFetchingAgreementTypes) {
            return <PageLoadingDots />;
        }

        if (!isNull(selectedAgreementTypeId)) {
            return <FewShotDocumentsTable agreementTypeId={selectedAgreementTypeId} aiAgreementTypes={aiAgreementTypes} />;
        }
        return <FewShotAgreementTypes aiAgreementTypes={aiAgreementTypes} selectAgreementType={selectAgreementType} />;
    }, [isFetchingAgreementTypes, selectedAgreementTypeId, aiAgreementTypes, selectAgreementType]);

    return (
        <div className={styles.fewShotWrapper}>
            {pageContent}
            <UploadModal isOpen={uploadModalIsOpen} closeModal={closeUploadModal} />
        </div>
    );
};

